import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { IAirRating } from "libraries/model/air-quote/IQuote";
import { Yii } from "libraries/interfaces/Yii";
import { LabelService } from "libraries/service/label/label.service";
import { EScaleColor, EScaleShape } from "libraries/component/rating-shape/rating-shape.component";
import { DecimalPipe } from "@angular/common";
import { LanguageService } from "libraries/service/language/language.service";
import { ICarbonFootPrintDifference } from "libraries/component/carbon-footprint/carbon-footprint.interface";

export interface IColorScale {
    min: number;
    max: number;
    color: EScaleColor;
    shape: EScaleShape;
}

@Component({
    selector: 'rating-notation',
    template: `
        <div class="rating-notation-container" *ngIf="rating" [class.small-margin]="showShape && !showNotation">
            <rating-shape [color]="color" [shape]="shape" *ngIf="showShape"></rating-shape>
            <div class="notation {{color}}" *ngIf="showNotation"><span [class.big]="!small" >{{notation}}</span></div>
        </div>
        <div class="average" *ngIf="average && showAverage">{{average}}</div>
        <div *ngIf="difference && !difference.isEqual">
            <strong class="difference"
                    [class.is-superior]="difference.isSuperior">
                {{difference.isSuperior ? "+" : "-"}}{{difference.value | number: digitsInfo:language}}
            </strong>
        </div>
    `,
    styleUrls: ['./rating-notation.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RatingNotationComponent implements OnChanges {
    @Input()
    public rating: IAirRating;
    @Input()
    public referenceRating: IAirRating;
    @Input()
    public showAverage: boolean = true;
    @Input()
    public small: boolean = false;
    @Input()
    public showShape: boolean = true;
    @Input()
    public showNotation: boolean = true;
    @Input()
    public averageLabel: string = Yii.t('wui-shared-js', '(%average% moy.)');

    @Input()
    public colorScales: IColorScale[] = [
        {min: 0, max: 3, color: EScaleColor.red, shape: EScaleShape.round},
        {min: 3, max: 5, color: EScaleColor.orange, shape: EScaleShape.round},
        {min: 5, max: 7, color: EScaleColor.yellow, shape: EScaleShape.round},
        {min: 7, max: 10, color: EScaleColor.green, shape: EScaleShape.round},
    ];

    public color: EScaleColor = EScaleColor.black;
    public shape: EScaleShape = EScaleShape.round;
    public notation: string;
    public average: string;
    public difference: ICarbonFootPrintDifference = null;
    public language: string = 'fr';
    public readonly digitsInfo: string = '1.2-2';

    constructor(private readonly labelService: LabelService,
                private readonly decimalPipe: DecimalPipe,
                private readonly languageService: LanguageService,
                ) {
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.rating && changes.rating.currentValue) {
            this.language = this.languageService.getUserLanguage();
            const rating: IAirRating = changes.rating.currentValue;
            const relatedColorScale: IColorScale = this.colorScales
                .find((colorScale: IColorScale) => {
                    return colorScale.min === rating.value || // value is equal to min value
                        (rating.value === colorScale.max && rating.value === rating.max) || // higher value
                        (rating.value > colorScale.min && rating.value < colorScale.max);
                });
            this.color = relatedColorScale ? relatedColorScale.color : EScaleColor.black;
            this.shape = relatedColorScale ? relatedColorScale.shape : EScaleShape.round;
            this.notation = rating.value || rating.value === 0
                ? `${this.decimalPipe.transform(rating.displayedValue || rating.value, this.digitsInfo, this.language)}/${rating.displayedMax || rating.max}`
                : null;
            this.average = rating.average || rating.average === 0 ?
                this.labelService.completeLabel(
                    this.averageLabel || Yii.t('wui-shared-js', '(%average% moy.)'),
                    {average: this.decimalPipe.transform(rating.average, this.digitsInfo, this.language), max: rating.max}
                ) :
                null;
        }

        if (changes && changes.referenceRating && changes.referenceRating.currentValue && this.rating) {
            const referenceRating: IAirRating = changes.referenceRating.currentValue;
            const isSuperior: boolean = +this.rating.value >= +referenceRating.value;
            const value: number = isSuperior ?
                +this.rating.value - +referenceRating.value :
                +referenceRating.value - +this.rating.value;
            this.difference = {
                value,
                isSuperior,
                isEqual: value === 0
            };
        }

    }
}
